import { Copy, Download } from 'lucide-react';
import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface ChatAiTableProps {
  children: React.ReactNode;
}

export function ChatAiTable({ children }: ChatAiTableProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [exported, setExported] = useState(false);
  const tableRef = useRef<HTMLTableElement>(null);

  const handleCopyTable = async () => {
    const tableElement = tableRef.current;
    if (tableElement) {
      // Extract headers
      const headers = Array.from(tableElement.querySelectorAll('thead th')).map(
        th => th.textContent?.trim() || ''
      );

      // Extract rows
      const rows = Array.from(tableElement.querySelectorAll('tbody tr')).map(tr =>
        Array.from(tr.querySelectorAll('td')).map(td => td.textContent?.trim() || '')
      );

      // Construct markdown table
      const headerRow = `| ${headers.join(' | ')} |`;
      const separatorRow = `| ${headers.map(() => '---').join(' | ')} |`;
      const dataRows = rows.map(row => `| ${row.join(' | ')} |`).join('\n');

      const markdownTable = [headerRow, separatorRow, dataRows].join('\n');

      await navigator.clipboard.writeText(markdownTable);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleExportCSV = () => {
    const tableElement = tableRef.current;
    if (tableElement) {
      const headers = Array.from(tableElement.querySelectorAll('thead th')).map(
        th => th.textContent?.trim() || ''
      );

      const rows = Array.from(tableElement.querySelectorAll('tbody tr')).map(tr =>
        Array.from(tr.querySelectorAll('td')).map(td => {
          const text = td.textContent?.trim() || '';
          // Escape quotes and wrap in quotes if contains comma, quote, or newline
          return text.includes(',') || text.includes('"') || text.includes('\n')
            ? `"${text.replace(/"/g, '""')}"`
            : text;
        })
      );

      // Construct CSV
      const csvContent = [
        headers
          .map(header =>
            header.includes(',') || header.includes('"') || header.includes('\n')
              ? `"${header.replace(/"/g, '""')}"`
              : header
          )
          .join(','),
        ...rows.map(row => row.join(',')),
      ].join('\n');

      // Create and trigger download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `table-export-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setExported(true);
      setTimeout(() => setExported(false), 2000);
    }
  };

  return (
    <div className="relative bg-white rounded-lg border border-gray-200 mb-4 overflow-hidden">
      <div className="flex items-center justify-end gap-2 px-4 py-2 bg-gray-50 border-b border-gray-200">
        <button
          onClick={handleExportCSV}
          className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded transition-colors"
          title={t('buttons.exportToCSV')}
          aria-label={t('buttons.exportToCSV')}
          data-testid="export-csv-button"
        >
          <Download size={12} />
          {exported ? t('buttons.exported') : t('buttons.export')}
        </button>

        <button
          onClick={handleCopyTable}
          className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded transition-colors"
          title={t('buttons.copy')}
          aria-label={t('buttons.copy')}
          data-testid="copy-table-button"
        >
          <Copy size={12} />
          {copied ? t('buttons.copied') : t('buttons.copy')}
        </button>
      </div>

      <div className="overflow-x-auto">
        <table ref={tableRef} className="w-full text-sm">
          {children}
        </table>
      </div>
    </div>
  );
}
